import { useState } from 'react';
import {
  Control,
  UseFormRegister,
  FieldErrors,
  FieldValues,
  FieldPath,
  useFieldArray,
  ArrayPath,
} from 'react-hook-form';

import { ItemForm } from './ItemForm';
import { ShareConfirmDialog } from './ShareConfirmDialog';

import Button from '@/components/Button/Button';
import DateField from '@/components/DateField/DateField';
import InputText from '@/components/InputText/InputText';
import { SupportPlanItem } from '@/types/supportPlan';

interface WithItems {
  items?: SupportPlanItem[];
}

type Props<T extends FieldValues & WithItems> = {
  errors: FieldErrors<T>;
  control: Control<T>;
  register: UseFormRegister<T>;
  childName: string;
  creatorName?: string;
  isEdit: boolean;
  isDraft?: boolean;
  onShare?: () => void;
};

export const BasicInfoForm = <T extends FieldValues & WithItems>({
  control,
  register,
  childName,
  creatorName,
  isEdit,
  isDraft,
  onShare,
}: Props<T>) => {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const typedRegister = (name: string) => register(name as FieldPath<T>);

  const { fields, append } = useFieldArray<T>({
    control,
    name: 'items' as ArrayPath<T>,
  });

  const addItem = () => {
    const newItem: SupportPlanItem = {
      category: 'personal_support',
      support_goal: '',
      support_content: '',
      five_area: 'health_life',
      achievement_period: 'three_months',
      support_provider: '',
      notes: '',
      priority: 1,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    append(newItem as any);
  };
  return (
    <div className="flex flex-col justify-center">
      <div className="flex flex-col">
        <div className="flex flex-row gap-4">
          <InputText
            label="利用者（児童）"
            type="text"
            required={false}
            placeholder={childName}
            disabled={true}
            {...typedRegister('child_id')}
          />
          <DateField
            label="作成年月日"
            required={false}
            control={control}
            name={'created_date' as FieldPath<T>}
          />
          <InputText
            label="作成者氏名"
            type="text"
            required={false}
            placeholder={creatorName}
            disabled={true}
            {...typedRegister('email')}
          />
        </div>

        <div className="flex flex-row gap-4">
          <InputText
            label="利用児及び家族の生活に対する意向"
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="textarea"
            rows={5}
            required={false}
            {...typedRegister('child_family_intentions')}
          />
        </div>
        <div className="flex flex-row gap-4">
          <InputText
            label="総合的な支援の方針"
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="textarea"
            required={false}
            {...typedRegister('general_policy')}
          />
        </div>
        <div className="flex flex-row gap-4">
          <InputText
            label="長期目標(内容・期間等)"
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="textarea"
            required={false}
            {...typedRegister('long_term_goal')}
          />
        </div>
        <div className="flex flex-row gap-4">
          <InputText
            label="短期目標(内容・期間等)"
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="textarea"
            required={false}
            {...typedRegister('short_term_goal')}
          />
          <InputText
            label="支援の標準的な提供時間等（曜日・頻度、時間）"
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="textarea"
            required={false}
            {...typedRegister('support_schedule_details')}
          />
        </div>
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="mb-4 rounded-md border p-4"
          >
            <ItemForm
              index={index}
              register={register}
              control={control}
            />
          </div>
        ))}

        <div className="mt-10 flex flex-row items-center justify-start space-x-2">
          <Button
            type="button"
            size="lg"
            variant="outline"
            onClick={addItem}
          >
            項目を追加
          </Button>
        </div>
        {isEdit && (
          <div className="mt-10 flex flex-row items-center justify-start space-x-2">
            <Button
              type="button"
              size="lg"
              variant="solid-fill"
              onClick={() => setIsShareDialogOpen(true)}
            >
              利用者へ共有
            </Button>
          </div>
        )}
      </div>

      {/* 共有確認ダイアログ */}
      <ShareConfirmDialog
        isOpen={isShareDialogOpen}
        setIsOpen={setIsShareDialogOpen}
        onShare={onShare}
        isDraft={isDraft}
      />
    </div>
  );
};
